import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { checkPermission } from '@/utils/permission';
import { Card, Col, Row, Space, Typography, Modal, Empty, Button, Tag, message } from 'antd';
import { CheckCircleOutlined, CloseOutlined, DeleteOutlined, EditOutlined, EyeOutlined } from '@ant-design/icons';
import TreatmentSessionForm from './TreatmentSessionForm';
import { approveTreatmentSession, getSpecificTreatmentSession, getTreatmentSessionCustomer, removeTreatmentSession, unapproveTreatmentSession } from '@/redux/actions/treatmentSessionActions';
import ReactHtmlParser from 'react-html-parser';
import TreatmentSessionDetail from './TreatmentSessionDetail';
import { formatTime, isValidDate } from '@/utils/helpers';
import { isMobile } from 'react-device-detect';
const { Text } = Typography;
const { confirm } = Modal;

const TreatmentSessionList = (props) => {

    const { customer, onCountChange } = props;
    const customerId = customer?.id;
    const dispatch = useDispatch();

    const [, setIsLoading] = useState(false);
    const [formVisible, setFormVisible] = useState(false);
    const [editingRecord, setEditingRecord] = useState(null);
    const [detailVisible, setDetailVisible] = useState(false);
    const [detailRecord, setDetailRecord] = useState(null);

    const allowUpdate = checkPermission('treatment_sessions', 'update');
    const allowDelete = checkPermission('treatment_sessions', 'remove');
    const allowDetail = checkPermission('treatment_sessions', 'detail');
    const allowApproved = checkPermission('treatment_sessions', 'approve');
    const allowUnapproved = checkPermission('treatment_sessions', 'unapprove');

    const [treatmentSessions, setTreatmentSessions] = useState([]);

    // load data trực tiếp
    const loadTreatmentSessions = async () => {
        try {
            setIsLoading(true);
            const res = await dispatch(getTreatmentSessionCustomer({}, customerId));
            // giả sử action trả về response dạng { data: [...] }
            setTreatmentSessions(res?.data?.data || []);
            onCountChange(res?.data?.data?.length || 0);
        } finally {
            setIsLoading(false);
        }
    };

    // gọi mỗi khi customerId thay đổi (navigation)
    useEffect(() => {
        if (customerId) {
            loadTreatmentSessions();
        }
    }, [customerId]);

    const handleCancel = () => {
        setFormVisible(false);
    };

    const colStyle = {
        borderRight: "1px solid #e8e8e8",
        padding: "12px",
        minHeight: "100%",
    };

    const handleDelete = (id) => {
        confirm({
            title: "Xác nhận xoá",
            content: "Bạn có chắc chắn muốn xoá phiếu điều trị này không?",
            okText: "Xoá",
            okType: "danger",
            cancelText: "Huỷ",
            async onOk() {
                try {
                    setIsLoading(true);
                    await dispatch(removeTreatmentSession([id])); // chờ xoá xong
                    await loadTreatmentSessions();                //  reload sau
                } finally {
                    setIsLoading(false);
                }
            },
        });
    };


    const handleOpenUpdateForm = async (id) => {
        try {
            const res = await dispatch(getSpecificTreatmentSession(id));
            setEditingRecord(res);   // gán dữ liệu trả về từ API
            setFormVisible(true);
        } finally {
        }
    };

    const handleOpenDetail = async (id) => {
        try {
            const res = await dispatch(getSpecificTreatmentSession(id));
            setDetailRecord(res);     // dữ liệu chi tiết
            setDetailVisible(true);        // mở popup
        } finally {
        }
    };

    const handleCloseDetail = () => {
        setDetailVisible(false);
    };

    const handleApprove = async (record) => {
        if (!record?.id) {
            message.error("Chưa có phiếu điều trị để xác nhận");
            return;
        }

        Modal.confirm({
            title: "Xác nhận",
            content: "Bạn có chắc muốn xác nhận phiếu điều trị?",
            okText: "Đồng ý",
            cancelText: "Hủy",
            onOk: async () => {
                setIsLoading(true);
                try {
                    await dispatch(approveTreatmentSession(record.id));
                    await loadTreatmentSessions(); // refresh lại data
                } finally {
                    setIsLoading(false);
                }
            }
        });
    };

    const handleUnapprove = async (record) => {
        if (!record?.id) return;

        Modal.confirm({
            title: "Xác nhận",
            content: "Bạn có chắc muốn hủy xác nhận phiếu điều trị?",
            okText: "Đồng ý",
            cancelText: "Hủy",
            onOk: async () => {
                setIsLoading(true);
                try {
                    await dispatch(unapproveTreatmentSession(record.id));
                    await loadTreatmentSessions(); // refresh lại data
                } finally {
                    setIsLoading(false);
                }
            }
        });
    };

    if (!treatmentSessions?.length) {
        return (
            <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={<span>Chưa có phiếu điều trị nào.</span>}
            />
        );
    }

    return (
        <div>
            <div>
                {
                    treatmentSessions.map((r, idx) => {
                        const isApproved = r.is_approved || false;
                        const created_user = r?.created_user;

                        return (
                            <Card
                                key={idx}
                                bordered
                                bodyStyle={{ padding: 0 }}
                                headStyle={{ padding: '0 12px' }}
                                style={{
                                    marginBottom: 24,
                                    border: '1px solid #e0e0e0',
                                    borderBottom: '5px solid transparent',
                                    borderImage: 'linear-gradient(to right, #d62400, #be1ac4)',
                                    borderImageSlice: 1,
                                    borderRadius: 6,
                                }}
                                title={
                                    <Space direction="vertical" size={0}>
                                        <Text>
                                            <Text strong>Ngày điều trị: </Text>
                                            {isValidDate(r.ngay, false)} {formatTime(r.gio)}
                                        </Text>
                                        <Text type="secondary">
                                            Lần khám:{' '}
                                            <Tag>
                                                {r.lan_kham === 'dieu_tri_moi'
                                                    ? 'Điều trị mới'
                                                    : 'Tái khám'}
                                            </Tag>
                                            &nbsp;–&nbsp;Địa điểm:{' '}
                                            <Tag>{r.branch?.name}</Tag>
                                        </Text>
                                    </Space>
                                }
                                extra={
                                    !isMobile && (
                                        <Space>
                                            <Button
                                                icon={<EyeOutlined />}
                                                size="small"
                                                onClick={() => handleOpenDetail(r.id)}
                                                disabled={!allowDetail}
                                            >
                                                Xem
                                            </Button>
                                            <Button
                                                icon={<EditOutlined />}
                                                size="small"
                                                onClick={() => handleOpenUpdateForm(r.id)}
                                                disabled={!allowUpdate || isApproved}
                                            >
                                                Sửa
                                            </Button>
                                            <Button
                                                icon={<DeleteOutlined />}
                                                size="small"
                                                danger
                                                onClick={() => handleDelete(r.id)}
                                                disabled={!allowDelete || isApproved}
                                            >
                                                Xóa
                                            </Button>
                                        </Space>
                                    )
                                }
                            >
                                <Row style={{ borderBottom: "1px solid #e8e8e8" }}>
                                    <Col xs={24} md={6} style={colStyle}>
                                        <div
                                            style={{
                                                whiteSpace: "pre-wrap",
                                                wordBreak: "break-word",
                                            }}
                                        >
                                            <Text strong>BS/Y tá phụ trách</Text>
                                            {r.doctors && r.doctors.length > 0 && (
                                                r.doctors.map((a, i) => (
                                                    <div key={i}>
                                                        <span style={{ color: '#999' }}>{a.doctor?.user_role?.name}: </span>
                                                        <span style={{ fontWeight: 400 }}>{a.doctor?.full_name}</span>
                                                    </div>
                                                ))
                                            )}
                                            <div>
                                                {r.assistants && r.assistants.length > 0 &&
                                                    r.assistants.map((a, i) => (
                                                        <div key={i}>
                                                            <span style={{ color: '#999' }}>{a.assistant?.user_role?.name}: </span>
                                                            <span style={{ fontWeight: 400 }}>{a.assistant?.full_name}</span>
                                                        </div>
                                                    ))
                                                }
                                            </div>
                                        </div>
                                    </Col>
                                    <Col xs={24} md={12} style={colStyle}>
                                        <div>
                                            <Text strong>Thủ thuật thực hiện</Text>
                                            <ul
                                                style={{
                                                    margin: 0,
                                                    paddingLeft: 18,
                                                    whiteSpace: "normal",
                                                    wordBreak: "break-word",
                                                }}
                                            >{r.procedures?.map((p, i) => (
                                                <li key={p.id || i}>
                                                    {p.name}
                                                    {p.description && (
                                                        <div style={{ fontSize: 12, color: '#888' }}>
                                                            {p.description}
                                                        </div>
                                                    )}
                                                </li>
                                            ))}
                                            </ul>
                                        </div>
                                        {r.don_thuoc && (
                                            <div className='mt-3'>
                                                <Text strong>Đơn thuốc</Text>
                                                <div>{ReactHtmlParser(r.don_thuoc || "")}</div>
                                            </div>
                                        )}
                                        {r.loi_dan && (
                                            <div className='mt-3'>
                                                <Text strong>Lời dặn</Text>
                                                <div>{ReactHtmlParser(r.loi_dan || "")}</div>
                                            </div>
                                        )}
                                    </Col>
                                    <Col xs={24} md={6} style={colStyle}>
                                        <Text strong>Dịch vụ</Text>
                                        <ul style={{ paddingLeft: "16px", margin: 0 }}>
                                            {r.customer_services && r.customer_services.length > 0 ? (
                                                r.customer_services.map((item) => (
                                                    <li key={item.id}>
                                                        <Text type="secondary">
                                                            {item.customer_service?.service?.name}
                                                        </Text>
                                                    </li>
                                                ))
                                            ) : (<></>)}
                                        </ul>
                                    </Col>
                                </Row>
                                <Row style={{ padding: "20px 12px" }} gutter={[10, 10]}>
                                    <Col xs={24} md={24}>
                                        <Text strong>Ghi chú (nội bộ): </Text>
                                        <Text>
                                            {ReactHtmlParser(r.ghi_chu_noi_bo || "")}
                                        </Text>
                                    </Col>
                                </Row>
                                {isMobile && (
                                    <Row
                                        style={{
                                            borderTop: '1px solid #f0f0f0',
                                            padding: 12,
                                        }}
                                        gutter={8}
                                    >
                                        {allowDetail && (
                                            <Col span={8}>
                                                <Button
                                                    block
                                                    icon={<EyeOutlined />}
                                                    onClick={() => handleOpenDetail(r.id)}
                                                >
                                                    Xem
                                                </Button>
                                            </Col>
                                        )}

                                        {allowUpdate && (
                                            <Col span={8}>
                                                <Button
                                                    block
                                                    icon={<EditOutlined />}
                                                    onClick={() => handleOpenUpdateForm(r.id)}
                                                    disabled={!allowUpdate || isApproved}
                                                >
                                                    Sửa
                                                </Button>
                                            </Col>
                                        )}

                                        {allowDelete && (
                                            <Col span={8}>
                                                <Button
                                                    block
                                                    danger
                                                    icon={<DeleteOutlined />}
                                                    onClick={() => handleDelete(r.id)}
                                                    disabled={!allowDelete || isApproved}
                                                >
                                                    Xóa
                                                </Button>
                                            </Col>
                                        )}
                                    </Row>
                                )}

                                <Row gutter={8} style={{ padding: "20px 12px" }}>
                                    <Col xs={24} md={24}>
                                        <div className='mt-4'>
                                            {isApproved ? (
                                                <>
                                                    <Space align="center" size="middle">
                                                        {allowUnapproved && (
                                                            <Button
                                                                htmlType="button"
                                                                icon={<CloseOutlined />}
                                                                onClick={() => handleUnapprove(r)}
                                                                danger
                                                                size="large"
                                                            >
                                                                Hủy xác nhận của bác sĩ
                                                            </Button>
                                                        )}

                                                        <Text style={{ color: 'rgba(0, 0, 0, 0.45)', fontStyle: 'italic' }}>
                                                            Xác nhận bởi: {r?.approved_by?.user_role?.name} - {r?.approved_by?.full_name} - {isValidDate(r?.approved_at, true)}
                                                        </Text>
                                                    </Space>
                                                </>
                                            ) : (
                                                allowApproved && (
                                                    <Button
                                                        type="primary"
                                                        style={{ background: "#52c41a", borderColor: "#52c41a" }}
                                                        icon={<CheckCircleOutlined />}
                                                        onClick={() => handleApprove(r)}
                                                        size="large"
                                                        disabled={false}
                                                    >
                                                        Xác nhận của bác sĩ
                                                    </Button>
                                                )
                                            )}

                                            <div style={{ fontStyle: 'italic' }} className='mt-3'>
                                                <strong>Ngày tạo:</strong> {isValidDate(r.created_at)}{' '}
                                                bởi:{''}
                                                {created_user && created_user.full_name && (
                                                    <span style={{ marginLeft: 8 }}>
                                                        {created_user.user_role?.name && (
                                                            <span style={{ color: "#999", fontSize: 13.5 }}>
                                                                {created_user.user_role.name}:{' '}
                                                            </span>
                                                        )}
                                                        <span style={{ fontWeight: 600, fontSize: 13.5 }}>
                                                            {created_user.full_name}
                                                        </span>
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                            </Card>
                        )
                    })
                }
            </div>
            <TreatmentSessionForm
                onSuccess={loadTreatmentSessions}
                record={editingRecord}
                customerId={customerId}
                visible={formVisible}
                onCancel={handleCancel}
            />
            <TreatmentSessionDetail visible={detailVisible} onClose={handleCloseDetail} record={detailRecord} />
        </div>
    );
};

export default TreatmentSessionList;
